import { Component, ElementRef, HostListener, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { BlindData, ContactForm, GalleryViewTypes, QuotationForm, QuotationFormPayload, QuotationPrice } from '@root/app.interfaces';
import { BREAKPOINTS, SELECTORS, STORAGE_NAMES, VIEW_TYPES } from '@root/app.config';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { ShareService } from '@core/services/share-data/share-data.service';
import { EngineService } from '@core/services/engine/engine.service';
import { iif, of, Subscription, SubscriptionLike, tap, switchMap } from 'rxjs';
import { ModalService } from '@core/services/modal/modal.service';
import { VerifyEmailModalComponent } from '@shared/modals/verify-email-modal/verify-email-modal.component';
import { MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { PayDepositModalComponent } from '@shared/modals/pay-deposit-modal/pay-deposit-modal.component';
import { DepositSuccessModalComponent } from '@shared/modals/deposit-success-modal/deposit-success-modal.component';
import { QuotationMobileModalComponent } from '@shared/modals/quotation-mobile-modal/quotation-mobile-modal.component';
import { ScrollService } from '@core/services/scroll/scroll.service';
import { PassDataService } from '@core/services/pass-data/pass-data.service';
import { Params, Router } from '@angular/router';
import { Location } from '@angular/common';
import { ServerDataService } from '@core/services/server-data/server-data.service';
import { VerificationTimerService } from '@core/services/verification-timer/verification-timer.service';
import { PaymentSessionServiceService } from '@core/services/payment-session-service/payment-session-service.service';
import { QuotationFormComponent } from '@features/quotation-form/quotation-form.component';

@Component({
	selector: 'app-quotation-page',
	templateUrl: './quotation-page.component.html',
	styleUrls: ['./quotation-page.component.scss', './quotation-page.responsive.scss'],
})
export class QuotationPageComponent<T, R> implements OnInit, OnDestroy {
	@ViewChild('payDepositBlock') payDepositBlock: ElementRef<HTMLElement>;
	@ViewChild('mobilePanel') mobilePanel: ElementRef<HTMLElement>;
	@ViewChild(QuotationFormComponent) quotationForm: QuotationFormComponent;

	protected readonly BREAKPOINTS = BREAKPOINTS;
	protected readonly GALLERY_VIEW_TYPE = GalleryViewTypes;

	private priceSubs: Subscription;
	blindsReceivedSub: Subscription;
	popStateSub: SubscriptionLike;
	loading = false;
	sendingCode = false;
	emailVerified = false;
	blinds: BlindData[];
	iconsFolder = '/assets/icons/';
	savedForm: QuotationForm;
	mobileQuoteDialog: MatLegacyDialogRef<T, R>;
	protected priceById: { [blindId: number]: QuotationPrice };
	protected priceDetails: { total: number; deposit: number };

	@HostListener('window:scroll') onScroll() {
		if (this.mobilePanel?.nativeElement && this.payDepositBlock?.nativeElement) {
			const { bottom, height } = this.payDepositBlock.nativeElement.getBoundingClientRect();
			const showPanel = bottom - height < 0;

			document.querySelector(SELECTORS.overview_bottom_panel).classList.toggle('hidden', !showPanel);
			document.querySelector(SELECTORS.top_button).classList.toggle('offsetted', showPanel);
		}
	}

	constructor(
		private router: Router,
		private location: Location,
		private modalService: ModalService,
		private shareService: ShareService,
		private engineService: EngineService,
		private scrollService: ScrollService,
		private passDataService: PassDataService,
		private serverDataService: ServerDataService,
		private sessionStorageService: SessionStorageService,
		private verificationTimerService: VerificationTimerService,
		private paymentSessionService: PaymentSessionServiceService,
	) {
		this.popStateSub = this.location.subscribe((event) => {
			if (event.pop && event.url.startsWith('/quote')) {
				const { href, pathname } = window.location;
				const queryParams = this.getQueryParamsFromString(href);

				setTimeout(() => {
					if (!this.emailVerified) {
						delete queryParams.deposit_modal;
					}

					this.router.navigate([pathname], { replaceUrl: true, queryParams }).then(() => {
						if (queryParams['deposit_modal'] && !this.paymentSessionService.isPaid()) {
							this.openPayDepositModal(this.savedForm);
						}
					});
				});
			}
		});
	}

	ngOnInit() {
		this.getBlindsFromStorage();
		this.getFormFromStorage();
		this.blindsReceivedFromSession();
		this.sceneHandler();
		this.setPriceDetailsSubs();

		this.emailVerified = this.passDataService.isShopFormVerified;
	}

	ngOnDestroy() {
		this.saveFormToStorage(this.quotationForm.getFormValues());
		this.blindsReceivedSub.unsubscribe();
		this.popStateSub.unsubscribe();
		this.priceSubs.unsubscribe();
	}

	onSubmit(form: QuotationForm): void {
		if (!this.emailVerified) {
			this.verifyEmailHandler(form);
			return;
		}

		this.openPayDepositModal(form);
	}

	getBlindsFromStorage(): void {
		this.blinds = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
	}

	blindsReceivedFromSession(): void {
		this.loading = true;
		this.blindsReceivedSub = this.shareService.getSessionBlindsData.subscribe(() => {
			this.blinds = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
			this.loading = false;
		});
	}

	onDeleteBlind({ blind_id }: BlindData): void {
		this.blinds = this.blinds.filter((blind) => blind.blind_id !== blind_id);
		if (this.blinds.length) {
			this.getPrice();
		}
		this.mobileQuoteDialog?.close();
	}

	getFormFromStorage(): void {
		this.savedForm = this.sessionStorageService.getSession(STORAGE_NAMES.zip_quotation_form);
	}

	saveFormToStorage(form: QuotationForm): void {
		this.sessionStorageService.setSession(form, STORAGE_NAMES.zip_quotation_form);
	}

	verifyEmailHandler(form: QuotationForm) {
		this.sendingCode = true;

		this.sendCodeIfNeeded(form)
			.pipe(switchMap(() => this.openVerifyModal(form).afterClosed()))
			.subscribe({
				next: (isVerified: boolean) => {
					this.emailVerified = isVerified;
					this.passDataService.isShopFormVerified = isVerified;
					this.saveFormToStorage(form);
					this.scrollService.scrollToTop();
				},
				error: (err) => {
					console.error(err);
					this.sendingCode = false;
				},
			});
	}

	private sendCodeIfNeeded(form: ContactForm) {
		return iif(
			() => this.verificationTimerService.isTimerActive,
			of(null),
			this.serverDataService.PostSendVerificationCode({ email: form.email }),
		).pipe(tap(() => (this.sendingCode = false)));
	}

	openVerifyModal<T, R>({ email }: ContactForm): MatLegacyDialogRef<T, R> {
		return this.modalService.openResponsiveDialog({
			component: VerifyEmailModalComponent,
			data: { email },
			...this.modalService.getConfig(VerifyEmailModalComponent.ID),
		});
	}

	openPayDepositModal(form: QuotationForm): void {
		this.updateDepositModalQueryParams(true);
		const dialogRef = this.modalService.openResponsiveDialog({
			...{ component: PayDepositModalComponent, data: { amount: this.priceDetails.deposit, contactForm: this.mapFormToPayload(form) } },
			...this.modalService.getConfig(PayDepositModalComponent.ID),
		});

		dialogRef.afterClosed().subscribe((success: boolean) => {
			this.openDepositSuccessModal(success);
			this.updateDepositModalQueryParams(false);
			if (success) {
				this.paymentSessionService.markAsPaid();
				this.passDataService.isShopFormVerified = false;
			}
		});
	}

	private mapFormToPayload(form: QuotationForm): QuotationFormPayload {
		if (!form) {
			return {} as QuotationFormPayload;
		}

		return {
			...form,
			country: form.country?.name,
			state: form.state?.title,
		};
	}

	updateDepositModalQueryParams(isDepositModalOpen: boolean): void {
		void this.router.navigate([], {
			queryParams: { deposit_modal: isDepositModalOpen || null },
			queryParamsHandling: 'merge',
		});
	}

	openDepositSuccessModal(status: boolean): void {
		if (!status) {
			return;
		}

		this.modalService.openResponsiveDialog({
			...{ component: DepositSuccessModalComponent },
			...this.modalService.getConfig(DepositSuccessModalComponent.ID),
		});
	}

	openQuote<T>(template: TemplateRef<T>): void {
		this.modalService.openResponsiveDialog({
			component: QuotationMobileModalComponent,
			data: template,
			closeOnNavigation: true,
			...this.modalService.getConfig(QuotationMobileModalComponent.ID),
		});
	}

	getQueryParamsFromString(url: string): Params | null {
		const urlObj = new URL(url);
		const params = {};
		for (const [key, value] of urlObj.searchParams.entries()) {
			params[key] = value;
		}
		return params;
	}

	getPrice(): void {
		const sessionKey = this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_config)?.session_key;

		if (sessionKey && this.passDataService.isShopFormVerified) {
			this.serverDataService.getPrice(sessionKey).subscribe({
				next: this.shareService.setPriceDetails.bind(this.shareService),
				error: (err) => {
					console.error(err);
				},
			});
		}
	}

	setPriceDetailsSubs(): void {
		this.priceSubs = this.shareService.getPriceDetails.subscribe((priceDetails) => {
			if (!priceDetails) {
				return;
			}
			this.priceById = priceDetails.blinds?.reduce((acc, item) => {
				acc[item.blind_id] = item.quotation;
				return acc;
			}, {});
			this.priceDetails = {
				total: priceDetails.total_price,
				deposit: priceDetails.deposit,
			};
		});
	}

	sceneHandler(): void {
		const getViewType = this.sessionStorageService.getSession(STORAGE_NAMES.zip_view_type);
		this.imageVisualisationStatusHandler(getViewType === VIEW_TYPES.image_visualisation || !getViewType);

		this.shareService.setTaskbarPanel(false);
		this.engineService.isModelCreated = false;
	}

	imageVisualisationStatusHandler(status: boolean): void {
		if (status) {
			this.sessionStorageService.setSession(true, STORAGE_NAMES.zip_image_visualisation_status);
		} else {
			this.sessionStorageService.removeData(STORAGE_NAMES.zip_image_visualisation_status);
		}
	}
}
